import React from "react";
import fileAPI from '../../api/fileAPI';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { useEffect, useState } from "react";
import { useParams } from "react-router";
import FileAPI from "../../api/fileAPI";
import TimeAgo from "react-timeago";
import { Link } from "react-router-dom";
import AuthLayout from "../../components/AuthLayout/AuthLayout";
import ResourceFiles from "../../components/Resource/Files";


import {
  S3_KEY,
  S3_REGION,
  S3_SECRET,
  S3_ENDPOINT,
  S3_BUCKET,
  MEDIA_URL,
} from "../../configs";
import { getS3DownloadHandler, renderStorageLinks, renderSingleStorageLink } from "../../helpers/files";
import format from "../../helpers/format";
import { useDispatch, useSelector } from "react-redux";
import { setMessage, setSuccessMessage } from "../../redux/actions/message";

dayjs.extend(relativeTime);

// Store verification results per storage index

const FilesDetail = () => {
  const { id } = useParams();
  const [file, setFile] = useState({});
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [relatedFile, setRelatedFile] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const { message, messageType } = useSelector((state) => state.message);


  const [verificationResults, setVerificationResults] = useState({});

  const verifyFileStorage = async (fileId, storageType, idx) => {
    setVerificationResults((prev) => ({ ...prev, [idx]: { loading: true } }));
    try {
      const res = await fileAPI.verifyFileExist(fileId, storageType);
      if (res && res.data) {
        setVerificationResults((prev) => ({ ...prev, [idx]: { loading: false, result: res.data } }));
      } else {
        setVerificationResults((prev) => ({ ...prev, [idx]: { loading: false, error: 'No verification data returned.' } }));
      }
    } catch (err) {
      setVerificationResults((prev) => ({ ...prev, [idx]: { loading: false, error: err?.response?.data?.detail || err.message } }));
    }
  };


  const fetchData = async () => {
    await FileAPI.getFileById(id)
      .then((res) => {
        setFile(res.data);
        setStatus(res.data.status);
      })
      .catch((err) => console.error(err));
    setLoading(false);
  };

  const fetchRelatedFiles = async () => {
    await FileAPI.getFilesSameResources(id)
      .then((response) => {
        setRelatedFile(response.data);
      })
      .catch((e) => {
      });
  };

  const downloadS3 = getS3DownloadHandler();

  useEffect(() => {
    fetchData();
    fetchRelatedFiles();
  }, [id]);

  const removeFile = async () => {
    setIsSubmitting(true);
    await FileAPI.deleteFile(file._id)
      .then((response) => {
        if (response.status === 204) {
          setStatus("deleted");
          dispatch(setMessage(`File ${file.filename} was deleted`, "success"));
        }
      })
      .catch((e) => {
        dispatch(setMessage(`${e}`, "danger"));
      });
    setIsSubmitting(false);
  };

  const activeFile = async () => {
    setIsSubmitting(true);
    await FileAPI.updateFile(file._id, { status: "active" })
      .then((response) => {
        if (response.status === 204) {
          setStatus("active");
          dispatch(
            setMessage(`File ${file.filename} was activated`, "success")
          );
        }
      })
      .catch((e) => {
        dispatch(setMessage(`${e}`, "danger"));
      });
    setIsSubmitting(false);
  };

  const hardDeleteFile = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete "${file.filename}"?\n\n` +
      "This action will:\n" +
      "• Remove the file from the database\n" +
      "• Delete all physical files from storage\n" +
      "• Cannot be undone\n\n" +
      "Type 'DELETE' to confirm"
    );
    
    if (!confirmed) return;
    
    // Additional confirmation with text input
    const deleteConfirmation = window.prompt(
      `To confirm permanent deletion of "${file.filename}", please type: DELETE`
    );
    
    if (deleteConfirmation !== "DELETE") {
      if (deleteConfirmation !== null) {
        dispatch(setMessage("Deletion cancelled - confirmation text did not match", "warning"));
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await FileAPI.hardDeleteFile(file._id);
      if (response.status === 200) {
        dispatch(setSuccessMessage(
          `File "${file.filename}" has been permanently deleted from database and storage`
        ));
        // Redirect to files list after successful deletion
        setTimeout(() => {
          window.location.href = "/files";
        }, 2000);
      }
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || e?.message || "Failed to delete file";
      dispatch(setMessage(`Hard delete failed: ${errorMsg}`, "danger"));
    }
    setIsSubmitting(false);
  };

  const deleteFromStorage = async (storageType) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this file from ${storageType.replace('_', ' ').toUpperCase()}?\n\n` +
      "This action will:\n" +
      "• Remove the file from this storage location only\n" +
      "• Keep the file in other storage locations\n" +
      "• Cannot be undone"
    );
    
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const response = await FileAPI.deleteFileFromStorage(file._id, storageType);
      dispatch(setSuccessMessage(
        `File successfully deleted from ${storageType.replace('_', ' ').toUpperCase()}`
      ));
      
      // Refresh file data to show updated storage locations
      await fetchData();
      
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || error?.message || "Failed to delete file from storage";
      dispatch(setMessage(`Delete failed: ${errorMsg}`, "danger"));
    }
    setIsSubmitting(false);
  };

  return (
    <AuthLayout>
        {loading ? (
          <div>Loading...</div>
        ) : Object.keys(file).length ? (
          <>
           <header
                className={"d-flex justify-content-between align-items-center mb-4"}
              >
            <h2 className="fs-5">{file?.filename}</h2>
            <div>
              {status === "deleted" && (
                <>
                  <button
                    className="btn btn-success me-2"
                    type="button"
                    onClick={() => activeFile()}
                    disabled={isSubmitting}
                  >
                    Active
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    type="button"
                    onClick={() => hardDeleteFile()}
                    disabled={isSubmitting}
                    title="Permanently delete file from database and storage"
                  >
                    <i className="bi bi-trash-fill me-1"></i>
                    Hard Delete
                  </button>
                </>
              )}
              {status === "active" && (
                <>
                  <button
                    className="btn btn-danger me-2"
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => removeFile()}
                  >
                    Disable
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    type="button"
                    onClick={() => hardDeleteFile()}
                    disabled={isSubmitting}
                    title="Permanently delete file from database and storage"
                  >
                    <i className="bi bi-trash-fill me-1"></i>
                    Hard Delete
                  </button>
                </>
              )}
            </div>
            </header>
            <div className="row">
             
              {message && (
                <div className="mb-4">
                  <div className={`alert alert-${messageType}`} role="alert">
                    {message}
                  </div>
                </div>
              )}
              <div className="col-6">
                <h6 className="col-12 mb-3">File detail</h6>
                <dl className="row">
                  <dt className="col-3 fw-normal text-muted">Status</dt>
                  <dd className="col-9">{format.formatStatus(status)}</dd>
                  <dt className="col-3 fw-normal text-muted">Storage</dt>
                  <dd className="col-9">
                    {renderStorageLinks(file)}
                  </dd>
                  <dt className="col-3 fw-normal text-muted">Resource</dt>
                  <dd className="col-9">
                    <Link to={`/resources/view/${file.resourceId}`}>
                      {file.resourceId}
                    </Link>
                  </dd>
                  <dt className="col-3 fw-normal text-muted">File size</dt>
                  <dd className="col-9 fw-normal">
                    {file.file_size > 0 ? (
                      <>
                        <span className="small fw-semibold">
                          {format.formatBytes(file.file_size)}
                        </span>
                      </>
                    ) : (
                      <>
                        <div
                          className="bg-danger d-inline-flex rounded-circle ml-auto"
                          style={{ width: "14px", height: "14px" }}
                        ></div>
                      </>
                    )}
                  </dd>
                  <dt className="col-3 fw-normal text-muted">Format</dt>
                  <dd className="col-9 small text-uppercase fw-semibold">
                    {file?.format}
                  </dd>
                  <dt className="col-3 fw-normal text-muted">Created at</dt>
                  <dd className="col-9">
                    <span>
                      {dayjs(file.created_at).format("YYYY-MM-DD HH:mm:ss")}
                    </span>
                    <span className="ms-2 badge rounded-pill bg-secondary bg-opacity-10 text-secondary fw-normal py-1 px-2">
                      <i className="bi bi-clock me-1"></i>
                      <span className="">
                        <TimeAgo date={file.created_at} />
                      </span>
                    </span>
                  </dd>
                  <dt className="col-3 fw-normal text-muted">Download</dt>
                  <dd className="col-9">
                    <i className="bi bi-download me-2 small"></i>{file.stats?.downloaded ? file.stats.downloaded.length : 0}
                  </dd>
                </dl>
                {/* Storage Locations Section */}
                {file.storage_locations && file.storage_locations.length > 0 && (
                  <>
                    <h6 className="col-12 mb-3 mt-4">Storage Locations</h6>
                    <div className="mb-2">
                      
                    </div>
                    {file.storage_locations.map((loc, idx) => (
                        <div key={loc.storage_type + '-' + (loc.key || loc.path || idx)} className="mb-3">
                          <div className="d-flex align-items-center justify-content-start mb-2 gap-2">
                            <div className="fw-semibold text-primary">{renderSingleStorageLink(loc.storage_type, file, loc)}</div>
                            <button
                              type="button"
                              className="btn text-danger btn-sm"
                              onClick={() => deleteFromStorage(loc.storage_type)}
                              disabled={isSubmitting}
                              title={`Delete file from ${loc.storage_type.replace('_', ' ').toUpperCase()}`}
                            >
                              <i className="bi bi-trash3 me-1"></i>
                              Remove file from storage
                            </button>
                          </div>
                          <dl className="row mb-0">
                            <dt className="col-3 fw-normal text-muted">Type</dt>
                            <dd className="col-9 text-break small text-uppercase">{loc.storage_type}</dd>
                            <dt className="col-3 fw-normal text-muted">Uploaded at</dt>
                            <dd className="col-9 text-break small">
                              <span>
                              {dayjs(loc.uploaded_at).format("YYYY-MM-DD HH:mm:ss")}
                            </span>
                            <span className="ms-2 badge rounded-pill bg-secondary bg-opacity-10 text-secondary fw-normal py-1 px-2">
                              <i className="bi bi-clock me-1"></i>
                              <span className="">
                                <TimeAgo date={loc.uploaded_at} />
                              </span>
                            </span>
                            </dd>
                            <dt className="col-3 fw-normal text-muted">Path / Key</dt>
                            <dd className="col-9 text-break small">{loc.key || loc.path || '-'}</dd>
                            <dt className="col-3 fw-normal text-muted">ID</dt>
                            <dd className="col-9 text-break small">{loc.file_id || '-'}</dd>
                          </dl>
                          <button
                              type="button"
                              className="btn btn-link btn-sm small px-0"
                              onClick={() => verifyFileStorage(file._id, loc.storage_type, idx)}
                              disabled={isSubmitting}
                              title={`Verify file in ${loc.storage_type.replace('_', ' ').toUpperCase()}`}
                            >
                              Verify File
                          </button>
                          {/* Verification result display */}
                          {verificationResults[idx] && (
                            <div className="mt-2">
                              {verificationResults[idx].loading ? (
                                <span className="text-info small">Verifying...</span>
                              ) : verificationResults[idx].error ? (
                                <span className="text-danger small">{verificationResults[idx].error}</span>
                              ) : verificationResults[idx].result ? (
                                <div className="alert alert-light py-2 px-3 mb-0 small">
                                  <strong>Verification:</strong> {verificationResults[idx].result.valid ? "Valid" : "Not Valid"} <br />
                                  <span className="text-break">{verificationResults[idx].result.detail}</span>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                    ))}
                  </>
                )}
                {file?.metaData && (
                  <>
                  <h6 className="col-12 mb-3">Metadata</h6>
                  <dl className="row">
                    <dt className="col-3 fw-normal text-muted">Document title</dt>
                    <dd className="col-9">{file.metaData.documentTitle}</dd>
                    <dt className="col-3 fw-normal text-muted">Author(s)</dt>
                    <dd className="col-9">
                      {file.metaData.author?.map((item, index) => {
                        if (index < file.metaData.author.length - 1) {
                          return (
                            <span key={index} className="pe-1">
                              {item},
                            </span>
                          );
                        } else {
                          return <span key={index}>{item}</span>;
                        }
                      })}
                    </dd>
                    {file.metaData.authorTitle && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Author Title</dt>
                        <dd className="col-9">{file.metaData.authorTitle}</dd>
                      </>
                    )}
                    {file.metaData.copyrightInfoURL && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Copyright Info URL</dt>
                        <dd className="col-9">
                          {file.metaData.copyrightInfoURL}
                        </dd>
                      </>
                    )}
                    {file.metaData.copyrightNotice && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Copyright Notice</dt>
                        <dd className="col-9">
                          {file.metaData.copyrightNotice}
                        </dd>
                      </>
                    )}
                    {file.metaData.copyringStatus && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Copyring Status</dt>
                        <dd className="col-9">
                          {file.metaData.copyringStatus}
                        </dd>
                      </>
                    )}
                    {file.metaData.description && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Description</dt>
                        <dd className="col-9">{file.metaData.description}</dd>
                      </>
                    )}
                    {file.metaData.descriptionWriter && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Description writer</dt>
                        <dd className="col-9">
                          {file.metaData.descriptionWriter}
                        </dd>
                      </>
                    )}
                    {file.metaData.format && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Format</dt>
                        <dd className="col-9">{file.metaData.format}</dd>
                      </>
                    )}
                    {file.metaData.rating && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Rating</dt>
                        <dd className="col-9">{file.metaData.rating}</dd>
                      </>
                    )}
                    {file.metaData.application && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Application</dt>
                        <dd className="col-9">{file.metaData.application}</dd>
                      </>
                    )}
                    
                    {file.metaData.creationDate && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Creation date</dt>
                        <dd className="col-9">
                          <span>
                            {dayjs(file.metaData.creationDate).format("YYYY-MM-DD HH:mm:ss")}
                          </span>
                          <span className="ms-2 badge rounded-pill bg-secondary bg-opacity-10 text-secondary fw-normal py-1 px-2">
                            <i className="bi bi-clock me-1"></i>
                            <span className="">
                              <TimeAgo date={file.metaData.creationDate} />
                            </span>
                          </span>
                        </dd>
                      </>
                    )}
                    {file.metaData.modificationDate && (
                      <>
                        <dt className="col-3 fw-normal text-muted">Modification date</dt>
                        <dd className="col-9">
                          <span>
                            {dayjs(file.metaData.modificationDate).format("YYYY-MM-DD HH:mm:ss")}
                          </span>
                          <span className="ms-2 badge rounded-pill bg-secondary bg-opacity-10 text-secondary fw-normal py-1 px-2">
                            <i className="bi bi-clock me-1"></i>
                            <span className="">
                              <TimeAgo date={file.metaData.modificationDate} />
                            </span>
                          </span>
                        </dd>
                      </>
                    )}
                    {file.metaData.instanceID && (
                      <>
                        <dt className="col-3">InstanceID</dt>
                        <dd className="col-9">{file.metaData.instanceID}</dd>
                      </>
                    )}
                    {file.metaData.derivedFromInstanceID && (
                      <>
                        <dt className="col-3">DerivedFromInstanceID</dt>
                        <dd className="col-9">
                          {file.metaData.derivedFromInstanceID}
                        </dd>
                      </>
                    )}
                    {file.metaData.historyAction && (
                      <>
                        <dt className="col-3">HistoryAction</dt>
                        <dd className="col-9">{file.metaData.historyAction}</dd>
                      </>
                    )}
                    {file.metaData.historyInstanceID && (
                      <>
                        <dt className="col-3">HistoryInstanceID</dt>
                        <dd className="col-9">
                          {file.metaData.historyInstanceID}
                        </dd>
                      </>
                    )}
                    {file.metaData.historyWhen && (
                      <>
                        <dt className="col-3">HistoryWhen</dt>
                        <dd className="col-9">{file.metaData.historyWhen}</dd>
                      </>
                    )}
                  </dl>
                  </>
                )}
              </div>
              <div className="col-6 mb-4">
                <h6 className="mb-4">Preview</h6>
                <div>
                  {file.format && file.path ? (
                    file.format.toLowerCase() === 'svg' ? (
                      <div className="border rounded p-2 bg-white" style={{ maxWidth: 400, minHeight: 100 }}>
                        <object
                          type="image/svg+xml"
                          data={`${MEDIA_URL}${file.path}`}
                          style={{ width: '100%', height: 300 }}
                          aria-label="SVG Preview"
                        >
                          Your browser does not support SVG preview.
                        </object>
                      </div>
                    // ) : file.format.toLowerCase() === 'ai' ? (
                    //   <div className="border rounded p-2 bg-white" style={{ maxWidth: 400, minHeight: 100 }}>
                    //     {`${MEDIA_URL}${file.path}`}
                    //     <object
                    //       type="application/pdf"
                    //       data={`${MEDIA_URL}${file.path}`}
                    //       style={{ width: '100%', height: 300 }}
                    //       aria-label="AI Preview as PDF"
                    //     >
                    //       Your browser does not support PDF preview.
                    //     </object>
                    //     <div className="small text-muted mt-2">AI files are shown as PDF preview if supported by your browser.</div>
                    //   </div>
                    ) : (
                      <span className="text-muted small">No preview available for this file type.</span>
                    )
                  ) : (
                    <span className="text-muted small">No preview available for this file type.</span>
                  )}
              </div>
            </div>
            </div>
            
            {/* File Preview Section */}
            
            <div className="row mt-5 gx-5">
              <div className="col-6">
                {relatedFile && relatedFile.length > 0 && (
                  <>
                    <h6 className="mb-4">Other files from same resources</h6>
                    <ResourceFiles resourceFiles={relatedFile} />
                  </>
                )}
              </div>
              
              
            </div>
          </>
        ) : (
          <>
            <p>{"Not found"}</p>
          </>
        )}
    </AuthLayout>
  );
};

export default FilesDetail;
